import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  FileText,
  BarChart3,
  TrendingUp,
  Shield,
  Download,
  RefreshCw,
  X,
  DollarSign,
  Building,
  ArrowRightLeft,
  PieChart,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Eye,
} from 'lucide-react';
import { getIfrsMapping, getIfrsStandards, getIfrsDifferences } from '../../shared/utils/comptabiliteService';
import { logger } from '../../shared/utils/logger';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';

const DEFAULT_IFRS_DATA = {
  overview: { exercice: '2024', conformity: 78, currency: getBrandDefaultCurrency() },
  standards: [
    {
      id: 'ias_1',
      code: 'IAS 1',
      titre: '',
      titreEn: 'Presentation of Financial Statements',
      status: 'applied',
      impacts: [
        { poste: '', modification: '', montant: 0 },
      ],
    },
    {
      id: 'ias_2',
      code: 'IAS 2',
      titre: '',
      titreEn: 'Inventories',
      status: 'applied',
      impacts: [
        { poste: '', modification: '', montant: 220000 },
      ],
    },
    {
      id: 'ias_16',
      code: 'IAS 16',
      titre: '',
      titreEn: 'Property, Plant and Equipment',
      status: 'applied',
      impacts: [
        { poste: '', modification: '', montant: 1250000 },
      ],
    },
    {
      id: 'ias_37',
      code: 'IAS 37',
      titre: '',
      titreEn: 'Provisions, Contingent Liabilities and Contingent Assets',
      status: 'applied',
      impacts: [
        { poste: '', modification: '', montant: -180000 },
      ],
    },
    {
      id: 'ifrs_15',
      code: 'IFRS 15',
      titre: '',
      titreEn: 'Revenue from Contracts with Customers',
      status: 'applied',
      impacts: [
        { poste: '', modification: '', montant: 450000 },
      ],
    },
    {
      id: 'ifrs_16',
      code: 'IFRS 16',
      titre: '',
      titreEn: 'Leases',
      status: 'in_progress',
      impacts: [
        { poste: '', modification: '', montant: 890000 },
      ],
    },
  ],
  reconciliation: {
    nscfToIfrs: [
      {
        posteKey: 'demo_recon_0_poste',
        montantNSCF: 4500000,
        montantIFRS: 4720000,
        ajustements: [{ adjKey: 'demo_recon_0_adj_0', montant: 220000 }],
      },
      {
        posteKey: 'demo_recon_1_poste',
        montantNSCF: 18500000,
        montantIFRS: 19750000,
        ajustements: [{ adjKey: 'demo_recon_1_adj_0', montant: 1250000 }],
      },
      {
        posteKey: 'demo_recon_2_poste',
        montantNSCF: 920000,
        montantIFRS: 740000,
        ajustements: [{ adjKey: 'demo_recon_2_adj_0', montant: -180000 }],
      },
      {
        posteKey: 'demo_recon_3_poste',
        montantNSCF: 3250000,
        montantIFRS: 3700000,
        ajustements: [{ adjKey: 'demo_recon_3_adj_0', montant: 450000 }],
      },
    ],
  },
  statements: {
    balanceSheet: {
      assets: {
        nonCurrent: [
          { itemKey: 'demo_item_immobilisations_corporelles', nscf: 18500000, ifrs: 19750000, difference: 1250000 },
          { itemKey: 'demo_item_immobilisations_incorporelles', nscf: 2100000, ifrs: 2100000, difference: 0 },
        ],
        current: [
          { itemKey: 'demo_item_stocks', nscf: 4500000, ifrs: 4720000, difference: 220000 },
          { itemKey: 'demo_item_cr_ances_clients', nscf: 6800000, ifrs: 6800000, difference: 0 },
          { itemKey: 'demo_item_tr_sorerie', nscf: 3200000, ifrs: 3200000, difference: 0 },
        ],
      },
      liabilities: { equity: [], nonCurrent: [], current: [] },
    },
    incomeStatement: [
      { itemKey: 'demo_item_chiffre_d_affaires', nscf: 42000000, ifrs: 42450000, difference: 450000 },
      { itemKey: 'demo_item_charges_d_exploitation', nscf: -35200000, ifrs: -35480000, difference: -280000 },
      { itemKey: 'demo_item_r_sultat_d_exploitation', nscf: 6800000, ifrs: 6970000, difference: 170000 },
      { itemKey: 'demo_item_r_sultat_net', nscf: 3250000, ifrs: 3700000, difference: 450000 },
    ],
    cashFlow: [
      { itemKey: 'demo_item_flux_op_rationnels', nscf: 5200000, ifrs: 5350000, difference: 150000 },
      { itemKey: 'demo_item_flux_d_investissement', nscf: -2100000, ifrs: -2100000, difference: 0 },
      { itemKey: 'demo_item_flux_de_financement', nscf: -1800000, ifrs: -1750000, difference: 50000 },
      { itemKey: 'demo_item_variation_nette_de_tr_sorerie', nscf: 1300000, ifrs: 1500000, difference: 200000 },
    ],
    soce: [
      { itemKey: 'demo_item_capital_social', opening: 10000000, movement: 0, closing: 10000000 },
      { itemKey: 'demo_item_r_serves', opening: 4500000, movement: 850000, closing: 5350000 },
      { itemKey: 'demo_item_report_nouveau', opening: 1200000, movement: 0, closing: 1200000 },
      { itemKey: 'demo_item_r_sultat_de_l_exercice', opening: 0, movement: 3700000, closing: 3700000 },
    ],
  },
  consolidation: {
    entities: [
      {
        nameKey: 'demo_entity_parent_algeria',
        countryKey: 'demo_country_algeria',
        ownership: 100,
        methodKey: 'integration_global',
        revenue: 42000000,
        assets: 34500000,
        currency: getBrandDefaultCurrency(),
      },
      {
        nameKey: 'demo_entity_subsidiary_tunisia',
        countryKey: 'demo_country_tunisia',
        ownership: 65,
        methodKey: 'integration_proportional',
        revenue: 8500000,
        assets: 6200000,
        currency: getBrandDefaultCurrency(),
      },
    ],
    eliminations: [
      { typeKey: 'demo_elim_intragroup_sales', montant: 1200000 },
      { typeKey: 'demo_elim_intragroup_receivables', montant: 850000 },
      { typeKey: 'demo_elim_internal_dividends', montant: 320000 },
    ],
  },
  mapping: {
    nscfToIfrs: [
      { nscfCode: '21', nscfLibelleKey: 'demo_map_immob_corp', ifrsCode: 'IAS 16', ifrsLibelleKey: 'demo_map_ppe' },
      { nscfCode: '31', nscfLibelleKey: 'demo_map_stocks', ifrsCode: 'IAS 2', ifrsLibelleKey: 'demo_map_inventories' },
      { nscfCode: '41', nscfLibelleKey: 'demo_map_clients', ifrsCode: 'IFRS 9', ifrsLibelleKey: 'demo_map_financial_instruments' },
      { nscfCode: '70', nscfLibelleKey: 'demo_map_sales', ifrsCode: 'IFRS 15', ifrsLibelleKey: 'demo_map_revenue' },
      { nscfCode: '16', nscfLibelleKey: 'demo_map_loans', ifrsCode: 'IFRS 9', ifrsLibelleKey: 'demo_map_financial_liabilities' },
    ],
  },
  compliance: {
    mandatoryDisclosures: [
      { standard: 'IAS 1', requirementKey: 'demo_req_accounting_policies', status: 'completed' },
      { standard: 'IFRS 15', requirementKey: 'demo_req_revenue_segments', status: 'completed' },
      { standard: 'IFRS 16', requirementKey: 'demo_req_lease_maturity', status: 'in_progress' },
      { standard: 'IAS 36', requirementKey: 'demo_req_goodwill_impairment', status: 'in_progress' },
    ],
    auditTrail: [
      { actionKey: 'demo_audit_recon_generated', userKey: 'demo_user_accountant', date: '2024-12-15' },
      { actionKey: 'demo_audit_consolidation_validated', userKey: 'demo_user_cfo', date: '2024-12-18' },
      { actionKey: 'demo_audit_export_draft', userKey: 'demo_user_accountant', date: '2024-12-20' },
    ],
  },
  conceptualDifferences: [],
};

const mapApiStandard = (row) => ({
  id: row.id,
  code: row.name || row.code || '—',
  titre: row.title || row.titre || '',
  titreEn: row.title || row.titreEn || '',
  status: 'applied',
  impacts: row.impacts || [],
});

const mapApiMapping = (row) => ({
  nscfCode: row.nscf_account_number || row.nscfCode || '—',
  nscfLibelle: row.nscf_account_name || row.nscfLibelle || '',
  ifrsCode: row.ifrs_account_number || row.ifrsCode || '—',
  ifrsLibelle: row.ifrs_account_name || row.ifrsLibelle || '',
});

const DEFAULT_ETATS_MODAL_SECTIONS = { primary: true, detail: false };
const DEFAULT_RECON_MODAL_SECTIONS = { overview: true, detail: false };

const EmptyTabState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-16 bg-white/5 rounded-xl border-2 border-dashed border-white/10">
    <Icon className="w-12 h-12 mx-auto mb-4 text-white/30" />
    <p className="text-white/80 font-medium">{title}</p>
    <p className="text-white/50 text-sm mt-2 max-w-md mx-auto">{description}</p>
  </div>
);

const IfrsSupportPage = () => {
  const { t, i18n } = useTranslation();
  const numberLocale = getBrandLocale(i18n.language);
  const p = (key, opts) => t(`modules.comptabilite.pages.ifrsSupportPage.${key}`, opts);

  const ifrsEtatsCards = useMemo(() => [
    { id: 'balance', icon: BarChart3, glow: 'from-blue-500 to-cyan-600', iconClass: 'text-blue-400', title: p('etat_balance_title'), sub: p('etat_balance_sub') },
    { id: 'income', icon: TrendingUp, glow: 'from-green-500 to-emerald-600', iconClass: 'text-green-400', title: p('etat_income_title'), sub: p('etat_income_sub') },
    { id: 'cashflow', icon: ArrowRightLeft, glow: 'from-purple-500 to-pink-600', iconClass: 'text-purple-400', title: p('etat_cashflow_title'), sub: p('etat_cashflow_sub') },
    { id: 'soce', icon: PieChart, glow: 'from-orange-500 to-red-600', iconClass: 'text-orange-400', title: p('etat_soce_title'), sub: p('etat_soce_sub') },
    { id: 'notes', icon: FileText, glow: 'from-indigo-500 to-purple-600', iconClass: 'text-indigo-400', title: p('etat_notes_title'), sub: p('etat_notes_sub'), fullWidth: true },
  ], [p]);

  const translateStandardTitle = useCallback(
    (std) => p(`demo_std_${std.id}_title`, { defaultValue: std.titreEn || std.titre }),
    [p]
  );
  const translateImpactPoste = useCallback(
    (stdId, idx, fallback) => p(`demo_std_${stdId}_impact_${idx}_poste`, { defaultValue: fallback }),
    [p]
  );
  const translateImpactMod = useCallback(
    (stdId, idx, fallback) => p(`demo_std_${stdId}_impact_${idx}_mod`, { defaultValue: fallback }),
    [p]
  );
  const translateReconPoste = useCallback(
    (idx, item) => p(item?.posteKey || `demo_recon_${idx}_poste`, { defaultValue: item?.posteNSCF || '' }),
    [p]
  );
  const translateAdjDesc = useCallback(
    (reconIdx, adjIdx, item) => p(item?.adjKey || `demo_recon_${reconIdx}_adj_${adjIdx}`, { defaultValue: item?.description || '' }),
    [p]
  );
  const translateStatementItem = useCallback(
    (row) => {
      if (row?.itemKey) return p(row.itemKey);
      const fallback = typeof row === 'object' ? row.item : row;
      return p(`demo_item_${String(fallback || '').toLowerCase().replace(/[^a-z0-9]+/g, '_')}`, { defaultValue: fallback });
    },
    [p]
  );
  const demoLabel = useCallback((key, fallback) => (key ? p(key) : fallback), [p]);
  const entityName = useCallback((entity) => demoLabel(entity.nameKey, entity.name), [demoLabel]);
  const entityCountry = useCallback((entity) => demoLabel(entity.countryKey, entity.country), [demoLabel]);
  const entityMethod = useCallback((entity) => demoLabel(entity.methodKey, entity.method), [demoLabel]);
  const elimTypeLabel = useCallback((elim) => demoLabel(elim.typeKey, elim.type), [demoLabel]);
  const disclosureRequirement = useCallback((item) => demoLabel(item.requirementKey, item.requirement), [demoLabel]);
  const auditActionLabel = useCallback((entry) => demoLabel(entry.actionKey, entry.action), [demoLabel]);
  const auditUserLabel = useCallback((entry) => demoLabel(entry.userKey, entry.user), [demoLabel]);
  const mappingNscfLabel = useCallback((row) => demoLabel(row.nscfLibelleKey, row.nscfLibelle), [demoLabel]);
  const mappingIfrsLabel = useCallback((row) => demoLabel(row.ifrsLibelleKey, row.ifrsLibelle), [demoLabel]);

  const [ifrsData, setIfrsData] = useState(DEFAULT_IFRS_DATA);
  const [ifrsActiveTab, setIfrsActiveTab] = useState('standards');
  const [showReconciliationNSCF, setShowReconciliationNSCF] = useState(false);
  const [showEtatsIFRS, setShowEtatsIFRS] = useState(false);
  const [selectedEtatType, setSelectedEtatType] = useState(null);
  const [etatsExpandedSections, setEtatsExpandedSections] = useState(DEFAULT_ETATS_MODAL_SECTIONS);
  const [reconExpandedSections, setReconExpandedSections] = useState(DEFAULT_RECON_MODAL_SECTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const openEtatsModal = () => {
    setSelectedEtatType(null);
    setEtatsExpandedSections(DEFAULT_ETATS_MODAL_SECTIONS);
    setShowEtatsIFRS(true);
  };

  const openReconciliationModal = () => {
    setReconExpandedSections(DEFAULT_RECON_MODAL_SECTIONS);
    setShowReconciliationNSCF(true);
  };

  const handleSelectEtat = (etatId) => {
    setSelectedEtatType(etatId);
    setEtatsExpandedSections({ primary: false, detail: true });
  };

  const goToStatementsTab = (etatId) => {
    setShowEtatsIFRS(false);
    setIfrsActiveTab('statements');
    if (etatId) setSelectedEtatType(etatId);
  };

  const handleExportEtat = async (etatId) => {
    const card = ifrsEtatsCards.find((c) => c.id === etatId);
    if (!card) return;
    try {
      let rows = [];
      if (etatId === 'balance') {
        const bs = ifrsData.statements?.balanceSheet?.assets || {};
        rows = [...(bs.nonCurrent || []), ...(bs.current || [])].map((r) => ({
          [p('col_item')]: translateStatementItem(r), [p('col_nscf')]: r.nscf, [p('col_ifrs')]: r.ifrs, [p('col_difference')]: r.difference,
        }));
      } else if (etatId === 'income') {
        rows = (ifrsData.statements?.incomeStatement || []).map((r) => ({
          [p('col_item')]: translateStatementItem(r), [p('col_nscf')]: r.nscf, [p('col_ifrs')]: r.ifrs, [p('col_difference')]: r.difference,
        }));
      } else if (etatId === 'cashflow') {
        rows = (ifrsData.statements?.cashFlow || []).map((r) => ({
          [p('col_item')]: translateStatementItem(r), [p('col_nscf')]: r.nscf, [p('col_ifrs')]: r.ifrs, [p('col_difference')]: r.difference,
        }));
      } else if (etatId === 'soce') {
        rows = (ifrsData.statements?.soce || []).map((r) => ({
          [p('col_item')]: translateStatementItem(r), [p('col_opening')]: r.opening, [p('col_movement')]: r.movement, [p('col_closing')]: r.closing,
        }));
      } else if (etatId === 'notes') {
        rows = (ifrsData.compliance?.mandatoryDisclosures || []).map((r) => ({
          [p('pdf_col_standard')]: r.standard, [p('pdf_col_requirement')]: disclosureRequirement(r), [p('pdf_col_status')]: r.status === 'completed' ? p('status_completed') : p('status_in_progress'),
        }));
      }
      await generateReportPDF(p('pdf_export_prefix', { title: card.title }), rows, { orientation: 'landscape' });
    } catch (error) {
      logger.error('Export IFRS état:', error);
    }
  };

  const renderComparisonTable = (rows, columns = ['item', 'nscf', 'ifrs', 'difference']) => (
    <div className="overflow-x-auto rounded-lg border border-white/10">
      <table className="w-full">
        <thead className="glass-table-head">
          <tr>
            <th className="px-6 py-4 text-left text-white font-semibold">{p('col_item')}</th>
            {columns.includes('nscf') && <th className="px-6 py-4 text-right text-white font-semibold">{p('col_nscf')}</th>}
            {columns.includes('ifrs') && <th className="px-6 py-4 text-right text-white font-semibold">{p('col_ifrs')}</th>}
            {columns.includes('difference') && <th className="px-6 py-4 text-right text-white font-semibold">{p('col_difference')}</th>}
            {columns.includes('opening') && <th className="px-6 py-4 text-right text-white font-semibold">{p('col_opening')}</th>}
            {columns.includes('movement') && <th className="px-6 py-4 text-right text-white font-semibold">{p('col_movement')}</th>}
            {columns.includes('closing') && <th className="px-6 py-4 text-right text-white font-semibold">{p('col_closing')}</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-white/5">
              <td className="px-6 py-4 text-white">{translateStatementItem(row.item || row.standard || row.requirement)}</td>
              {columns.includes('nscf') && (
                <td className={`px-6 py-4 text-right font-mono ${row.nscf >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                  {row.nscf?.toLocaleString(numberLocale)}
                </td>
              )}
              {columns.includes('ifrs') && (
                <td className={`px-6 py-4 text-right font-mono ${row.ifrs >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {row.ifrs?.toLocaleString(numberLocale)}
                </td>
              )}
              {columns.includes('difference') && (
                <td className={`px-6 py-4 text-right font-mono font-semibold ${row.difference >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {row.difference >= 0 ? '+' : ''}{row.difference?.toLocaleString(numberLocale)}
                </td>
              )}
              {columns.includes('opening') && <td className="px-6 py-4 text-right font-mono text-white/80">{row.opening?.toLocaleString(numberLocale)}</td>}
              {columns.includes('movement') && <td className="px-6 py-4 text-right font-mono text-white/80">{row.movement?.toLocaleString(numberLocale)}</td>}
              {columns.includes('closing') && <td className="px-6 py-4 text-right font-mono text-white">{row.closing?.toLocaleString(numberLocale)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderEtatDetailContent = () => {
    if (!selectedEtatType) {
      return (
        <p className="text-white/50 text-sm text-center py-8">
          {p('select_statement_hint')}
        </p>
      );
    }
    if (selectedEtatType === 'balance') {
      const nonCurrent = ifrsData.statements?.balanceSheet?.assets?.nonCurrent || [];
      const current = ifrsData.statements?.balanceSheet?.assets?.current || [];
      return (
        <div className="space-y-4">
          {nonCurrent.length > 0 && (
            <div>
              <h5 className="text-white/80 font-medium mb-2">{p('non_current_assets_title')}</h5>
              {renderComparisonTable(nonCurrent)}
            </div>
          )}
          {current.length > 0 && (
            <div>
              <h5 className="text-white/80 font-medium mb-2">{p('current_assets_title')}</h5>
              {renderComparisonTable(current)}
            </div>
          )}
        </div>
      );
    }
    if (selectedEtatType === 'income') {
      return renderComparisonTable(ifrsData.statements?.incomeStatement || []);
    }
    if (selectedEtatType === 'cashflow') {
      return renderComparisonTable(ifrsData.statements?.cashFlow || []);
    }
    if (selectedEtatType === 'soce') {
      return renderComparisonTable(ifrsData.statements?.soce || [], ['item', 'opening', 'movement', 'closing']);
    }
    if (selectedEtatType === 'notes') {
      const rows = ifrsData.compliance?.mandatoryDisclosures || [];
      return (
        <div className="space-y-3">
          {rows.map((d, i) => (
            <div key={i} className="flex justify-between items-center bg-white/5 rounded-xl p-4 gap-4 flex-wrap">
              <div>
                <span className="text-indigo-400 font-semibold">{d.standard}</span>
                <p className="text-white/80 text-sm mt-1">{d.requirement}</p>
              </div>
              <span className={`px-3 py-1 rounded-lg text-xs font-medium ${d.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {d.status === 'completed' ? p('status_completed') : p('status_in_progress')}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const loadIfrsData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [standardsRes, mappingsRes, differencesRes] = await Promise.all([
        getIfrsStandards().catch(() => []),
        getIfrsMapping().catch(() => []),
        getIfrsDifferences().catch(() => []),
      ]);

      setIfrsData((prev) => {
        const next = { ...prev, ...DEFAULT_IFRS_DATA };
        const apiStandards = (standardsRes || []).map(mapApiStandard);
        if (apiStandards.length > 0) {
          next.standards = apiStandards.map((s) => {
            const fallback = DEFAULT_IFRS_DATA.standards.find((d) => d.code === s.code);
            return fallback ? { ...fallback, ...s, impacts: s.impacts?.length ? s.impacts : fallback.impacts } : s;
          });
        }
        const apiMappings = (mappingsRes || []).map(mapApiMapping);
        if (apiMappings.length > 0) {
          next.mapping = { nscfToIfrs: apiMappings };
        }
        if ((differencesRes || []).length > 0) {
          next.conceptualDifferences = differencesRes;
        }
        const appliedCount = next.standards.filter((s) => s.status === 'applied').length;
        next.overview = {
          ...next.overview,
          conformity: next.standards.length > 0 ? Math.round((appliedCount / next.standards.length) * 100) : 0,
        };
        return next;
      });
    } catch (error) {
      logger.error('Error loading IFRS data:', error);
      setLoadError(p('load_error'));
      setIfrsData(DEFAULT_IFRS_DATA);
    }
    setIsLoading(false);
  }, [p]);

  useEffect(() => {
    loadIfrsData();
  }, [loadIfrsData]);

  const tabs = useMemo(() => [
    { id: 'standards', labelKey: 'tab_standards', icon: BookOpen },
    { id: 'reconciliation', labelKey: 'tab_reconciliation', icon: RefreshCw },
    { id: 'statements', labelKey: 'tab_statements', icon: BarChart3 },
    { id: 'consolidation', labelKey: 'tab_consolidation', icon: Building },
    { id: 'mapping', labelKey: 'tab_mapping', icon: ArrowRightLeft },
    { id: 'compliance', labelKey: 'tab_compliance', icon: Shield },
  ], [t]);

  const overviewStatCards = useMemo(() => [
    { titleKey: 'stat_conformity', value: `${ifrsData.overview.conformity}%`, icon: Shield, color: 'from-green-500 to-green-600' },
    { titleKey: 'stat_standards_applied', value: (ifrsData.standards || []).filter((s) => s.status === 'applied').length, icon: BookOpen, color: 'from-blue-500 to-blue-600' },
    { titleKey: 'stat_entities_consolidated', value: (ifrsData.consolidation?.entities || []).length, icon: Building, color: 'from-purple-500 to-purple-600' },
    { titleKey: 'stat_reference_currency', value: ifrsData.overview.currency, icon: DollarSign, color: 'from-yellow-500 to-yellow-600' },
  ], [ifrsData, t]);

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
        </div>
      );
    }

    if (ifrsActiveTab === 'standards') {
      const items = ifrsData.standards || [];
      return (
        <div className="space-y-4">
          {loadError && (
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 text-orange-200 text-sm">
              <AlertCircle size={16} />
              {loadError}
            </div>
          )}
          <h3 className="text-lg font-semibold text-white mb-4">{p('standards_applied_title')}</h3>
          {items.length === 0 ? (
            <EmptyTabState
              icon={BookOpen}
              title={p('empty_standards_title')}
              description={p('empty_standards_description')}
            />
          ) : (
            items.map((standard) => (
              <div key={standard.id} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-lg font-semibold text-sm">
                        {standard.code}
                      </span>
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        standard.status === 'applied'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {standard.status === 'applied' ? p('status_applied') : p('status_in_progress')}
                      </span>
                    </div>
                    <h4 className="text-white font-medium">{translateStandardTitle(standard)}</h4>
                  </div>
                </div>
                {(standard.impacts || []).length > 0 && (
                  <div className="space-y-3">
                    <h5 className="text-white/80 font-medium text-sm">{p('impacts_on_statements')}</h5>
                    {standard.impacts.map((impact, index) => (
                      <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
                        <div>
                          <span className="text-white font-medium">{translateImpactPoste(standard.id, index, impact.poste)}</span>
                          <p className="text-white/60 text-sm">{translateImpactMod(standard.id, index, impact.modification)}</p>
                        </div>
                        <span className={`font-bold ${impact.montant >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {impact.montant >= 0 ? '+' : ''}{impact.montant.toLocaleString(numberLocale)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      );
    }

    if (ifrsActiveTab === 'reconciliation') {
      const items = ifrsData.reconciliation?.nscfToIfrs || [];
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white mb-4">{p('reconciliation_nscf_to_ifrs')}</h3>
          {items.length === 0 ? (
            <EmptyTabState
              icon={RefreshCw}
              title={p('empty_reconciliation_title')}
              description={p('empty_reconciliation_description')}
            />
          ) : (
            items.map((item, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                  <h4 className="text-white font-medium">{translateReconPoste(index, item)}</h4>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-blue-400">NSCF: {item.montantNSCF.toLocaleString(numberLocale)}</span>
                    <span className="text-green-400">IFRS: {item.montantIFRS.toLocaleString(numberLocale)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <h5 className="text-white/80 font-medium text-sm">{p('adjustments')}</h5>
                  {(item.ajustements || []).map((ajustement, ajIndex) => (
                    <div key={ajIndex} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
                      <span className="text-white/80">{translateAdjDesc(index, ajIndex, ajustement)}</span>
                      <span className={`font-bold ${ajustement.montant >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {ajustement.montant >= 0 ? '+' : ''}{ajustement.montant.toLocaleString(numberLocale)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
          {(ifrsData.conceptualDifferences || []).length > 0 && (
            <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4">{p('conceptual_differences_title')}</h4>
              <div className="space-y-3">
                {ifrsData.conceptualDifferences.map((diff, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-4">
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <span className="text-white font-medium">{diff.category}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        diff.impact === 'High' ? 'bg-red-500/20 text-red-300' : 'bg-orange-500/20 text-orange-300'
                      }`}>
                        {p('impact_level', { impact: diff.impact })}
                      </span>
                    </div>
                    <p className="text-white/60 text-sm mt-2">NSCF: {diff.nscf_approach}</p>
                    <p className="text-white/60 text-sm">IFRS: {diff.ifrs_approach}</p>
                    {diff.notes && <p className="text-white/40 text-xs mt-1 italic">{diff.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (ifrsActiveTab === 'statements') {
      const nonCurrent = ifrsData.statements?.balanceSheet?.assets?.nonCurrent || [];
      const current = ifrsData.statements?.balanceSheet?.assets?.current || [];
      const income = ifrsData.statements?.incomeStatement || [];
      const hasData = nonCurrent.length + current.length + income.length > 0;

      return (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white mb-4">{p('statements_ifrs_vs_nscf')}</h3>
          {!hasData ? (
            <EmptyTabState
              icon={BarChart3}
              title={p('empty_statements_title')}
              description={p('empty_statements_description')}
            />
          ) : (
            <>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h4 className="text-white font-medium mb-4">{p('consolidated_balance_comparison')}</h4>
                <div className="grid grid-cols-4 gap-4 text-sm font-medium text-white/80 border-b border-white/10 pb-2 mb-3">
                  <span>{p('col_item')}</span>
                  <span className="text-right">{p('col_nscf')}</span>
                  <span className="text-right">{p('col_ifrs')}</span>
                  <span className="text-right">{p('col_difference')}</span>
                </div>
                {nonCurrent.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-white/80 font-medium mb-3">{p('non_current_assets')}</h5>
                    <div className="space-y-2">
                      {nonCurrent.map((asset, index) => (
                        <div key={index} className="grid grid-cols-4 gap-4 bg-white/5 rounded-lg p-3 text-sm">
                          <span className="text-white">{translateStatementItem(asset)}</span>
                          <span className="text-blue-400 text-right">{asset.nscf.toLocaleString(numberLocale)}</span>
                          <span className="text-green-400 text-right">{asset.ifrs.toLocaleString(numberLocale)}</span>
                          <span className={`text-right font-bold ${asset.difference >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {asset.difference >= 0 ? '+' : ''}{asset.difference.toLocaleString(numberLocale)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {current.length > 0 && (
                  <div>
                    <h5 className="text-white/80 font-medium mb-3">{p('current_assets')}</h5>
                    <div className="space-y-2">
                      {current.map((asset, index) => (
                        <div key={index} className="grid grid-cols-4 gap-4 bg-white/5 rounded-lg p-3 text-sm">
                          <span className="text-white">{translateStatementItem(asset)}</span>
                          <span className="text-blue-400 text-right">{asset.nscf.toLocaleString(numberLocale)}</span>
                          <span className="text-green-400 text-right">{asset.ifrs.toLocaleString(numberLocale)}</span>
                          <span className={`text-right font-bold ${asset.difference >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {asset.difference >= 0 ? '+' : ''}{asset.difference.toLocaleString(numberLocale)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {income.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-medium mb-4">{p('income_statement_comparison')}</h4>
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-4 text-sm font-medium text-white/80 border-b border-white/10 pb-2">
                      <span>{p('col_item')}</span>
                      <span className="text-right">{p('col_nscf')}</span>
                      <span className="text-right">{p('col_ifrs')}</span>
                      <span className="text-right">{p('col_difference')}</span>
                    </div>
                    {income.map((item, index) => (
                      <div key={index} className="grid grid-cols-4 gap-4 bg-white/5 rounded-lg p-3 text-sm">
                        <span className="text-white">{translateStatementItem(item)}</span>
                        <span className={`text-right ${item.nscf >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {item.nscf.toLocaleString(numberLocale)}
                        </span>
                        <span className={`text-right ${item.ifrs >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {item.ifrs.toLocaleString(numberLocale)}
                        </span>
                        <span className={`text-right font-bold ${item.difference >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {item.difference >= 0 ? '+' : ''}{item.difference.toLocaleString(numberLocale)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    if (ifrsActiveTab === 'consolidation') {
      const entities = ifrsData.consolidation?.entities || [];
      const eliminations = ifrsData.consolidation?.eliminations || [];
      return (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white mb-4">{p('consolidation_scope')}</h3>
          {entities.length === 0 ? (
            <EmptyTabState
              icon={Building}
              title={p('empty_consolidation_title')}
              description={p('empty_consolidation_description')}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {entities.map((entity, index) => (
                <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-white font-medium">{entityName(entity)}</h4>
                      <p className="text-white/60 text-sm">{entityCountry(entity)}</p>
                    </div>
                    <span className="px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-lg text-sm font-medium">
                      {entity.ownership}%
                    </span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/70">{p('method_label')}</span>
                      <span className="text-white font-medium">{entityMethod(entity)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">{p('revenue_label')}</span>
                      <span className="text-green-400">{entity.revenue.toLocaleString(numberLocale)} {entity.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">{p('total_assets_label')}</span>
                      <span className="text-blue-400">{entity.assets.toLocaleString(numberLocale)} {entity.currency}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {eliminations.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h4 className="text-white font-medium mb-4">{p('intragroup_eliminations')}</h4>
              <div className="space-y-3">
                {eliminations.map((elimination, index) => (
                  <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-3">
                    <span className="text-white">{elimTypeLabel(elimination)}</span>
                    <span className="text-orange-400 font-bold">
                      -{elimination.montant.toLocaleString(numberLocale)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (ifrsActiveTab === 'mapping') {
      const rows = ifrsData.mapping?.nscfToIfrs || [];
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white mb-4">{p('mapping_nscf_ifrs')}</h3>
          {rows.length === 0 ? (
            <EmptyTabState
              icon={ArrowRightLeft}
              title={p('empty_mapping_title')}
              description={p('empty_mapping_description')}
            />
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="glass-table-head">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_nscf_code')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_nscf_label')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_ifrs_standard')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_ifrs_label')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {rows.map((mapping, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-6 py-4 text-blue-400 font-mono">{mapping.nscfCode}</td>
                      <td className="px-6 py-4 text-white">{mappingNscfLabel(mapping)}</td>
                      <td className="px-6 py-4 text-indigo-400 font-medium">{mapping.ifrsCode}</td>
                      <td className="px-6 py-4 text-white/80">{mappingIfrsLabel(mapping)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (ifrsActiveTab === 'compliance') {
      const disclosures = ifrsData.compliance?.mandatoryDisclosures || [];
      const auditTrail = ifrsData.compliance?.auditTrail || [];
      return (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-white mb-4">{p('compliance_controls_ifrs')}</h3>
          {disclosures.length === 0 && auditTrail.length === 0 ? (
            <EmptyTabState
              icon={Shield}
              title={p('empty_compliance_title')}
              description={p('empty_compliance_description')}
            />
          ) : (
            <>
              {disclosures.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-medium mb-4">{p('mandatory_annex_disclosures')}</h4>
                  <div className="space-y-3">
                    {disclosures.map((disclosure, index) => (
                      <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-4 gap-4 flex-wrap">
                        <div>
                          <span className="text-white font-medium">{disclosure.standard}</span>
                          <p className="text-white/60 text-sm">{disclosureRequirement(disclosure)}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                          disclosure.status === 'completed'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {disclosure.status === 'completed' ? p('status_completed') : p('status_in_progress')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {auditTrail.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-white font-medium mb-4">{p('ifrs_audit_trail')}</h4>
                  <div className="space-y-3">
                    {auditTrail.map((entry, index) => (
                      <div key={index} className="flex justify-between items-center bg-white/5 rounded-lg p-4 flex-wrap gap-2">
                        <div>
                          <span className="text-white font-medium">{auditActionLabel(entry)}</span>
                          <p className="text-white/60 text-sm">{p('by_user', { user: auditUserLabel(entry) })}</p>
                        </div>
                        <span className="text-white/80 text-sm">{entry.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">{p('title')}</h2>
            <p className="text-white/60 text-sm">
              {p('subtitle', { exercice: ifrsData.overview.exercice })}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={openReconciliationModal}
              className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-2 rounded-xl font-medium hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
            >
              <RefreshCw size={16} />
              <span>{p('btn_reconciliation_nscf')}</span>
            </button>
            <button
              type="button"
              onClick={openEtatsModal}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-purple-500/30"
            >
              <Download size={16} />
              <span>{p('btn_etats_ifrs')}</span>
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {overviewStatCards.slice(0, 4).map((stat, i) => (
              <div key={i} className="relative group">
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white/60 text-sm font-medium">{p(stat.titleKey)}</h3>
                      <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border-b border-white/10 m-2 mb-0">
              <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setIfrsActiveTab(tab.id)}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                      ifrsActiveTab === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-5 w-5" />
                    <span>{p(tab.labelKey)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">{renderTabContent()}</div>
          </div>
        </div>
      </div>

      {showReconciliationNSCF && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowReconciliationNSCF(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '960px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <RefreshCw className="h-6 w-6 text-emerald-400" />
                {p('recon_modal_title')}
              </h2>
              <button type="button" onClick={() => setShowReconciliationNSCF(false)} className="text-white/70 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1 space-y-4">
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setReconExpandedSections((p) => ({ ...p, overview: !p.overview }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-3">
                    <BarChart3 size={20} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{p('overview')}</h3>
                  </div>
                  {reconExpandedSections.overview ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                </button>
                <AnimatePresence>
                  {reconExpandedSections.overview && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                          { titleKey: 'recon_stat_nscf_items', value: (ifrsData.reconciliation?.nscfToIfrs || []).length, glow: 'from-blue-500 to-blue-600' },
                          { titleKey: 'recon_stat_ifrs_adjustments', value: (ifrsData.reconciliation?.nscfToIfrs || []).reduce((s, r) => s + (r.ajustements?.length || 0), 0), glow: 'from-green-500 to-green-600' },
                          { titleKey: 'recon_stat_total_gap', value: (ifrsData.reconciliation?.nscfToIfrs || []).reduce((s, r) => s + (r.montantIFRS - r.montantNSCF), 0).toLocaleString(numberLocale), glow: 'from-purple-500 to-purple-600' },
                        ].slice(0, 4).map((stat, i) => (
                          <div key={i} className="relative group">
                            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.glow} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                              <p className="text-white/60 text-sm font-medium">{p(stat.titleKey)}</p>
                              <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setReconExpandedSections((p) => ({ ...p, detail: !p.detail }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-3">
                    <ArrowRightLeft size={20} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">{p('detail_items_title')}</h3>
                  </div>
                  {reconExpandedSections.detail ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                </button>
                <AnimatePresence>
                  {reconExpandedSections.detail && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0 space-y-3 max-h-64 overflow-y-auto">
                        {(ifrsData.reconciliation?.nscfToIfrs || []).map((item, index) => (
                          <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                            <div className="flex justify-between flex-wrap gap-2 mb-2">
                              <span className="text-white font-medium">{translateReconPoste(index, item)}</span>
                              <span className="text-green-400 text-sm font-mono">{(item.montantIFRS - item.montantNSCF).toLocaleString(numberLocale)}</span>
                            </div>
                            <p className="text-white/50 text-xs">
                              {p('nscf_to_ifrs', {
                                nscf: item.montantNSCF.toLocaleString(numberLocale),
                                ifrs: item.montantIFRS.toLocaleString(numberLocale),
                              })}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-blue-300 text-sm">
                  <strong>{p('note_prefix')}</strong> {p('recon_modal_note')}
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap">
              <button type="button" onClick={() => { setShowReconciliationNSCF(false); setIfrsActiveTab('reconciliation'); }} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all font-medium flex items-center gap-2">
                <Eye size={16} />
                {p('view_reconciliation_tab')}
              </button>
              <button type="button" onClick={() => { setShowReconciliationNSCF(false); setIfrsActiveTab('reconciliation'); }} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold shadow-lg shadow-blue-500/30 flex items-center gap-2">
                <RefreshCw size={16} />
                {p('open_reconciliation')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showEtatsIFRS && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowEtatsIFRS(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '960px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FileText className="h-6 w-6 text-indigo-400" />
                {p('etats_modal_title')}
              </h2>
              <button type="button" onClick={() => setShowEtatsIFRS(false)} className="text-white/70 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1 space-y-4">
              {/* Section 1 — États primaires */}
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setEtatsExpandedSections((p) => ({ ...p, primary: !p.primary }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-3">
                    <BarChart3 size={20} className="text-indigo-400" />
                    <h3 className="text-lg font-semibold text-white">{p('primary_financial_statements')}</h3>
                  </div>
                  {etatsExpandedSections.primary ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                </button>
                <AnimatePresence>
                  {etatsExpandedSections.primary && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {ifrsEtatsCards.map((card) => (
                          <div key={card.id} className={`relative group ${card.fullWidth ? 'md:col-span-2' : ''}`}>
                            <div className={`absolute -inset-0.5 bg-gradient-to-r ${card.glow} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                            <button
                              type="button"
                              onClick={() => handleSelectEtat(card.id)}
                              className={`relative w-full bg-white/10 backdrop-blur-lg rounded-3xl p-6 border text-left transition-all duration-300 hover:border-white/40 ${
                                selectedEtatType === card.id ? 'border-blue-400/60 ring-2 ring-blue-500/30 shadow-lg shadow-blue-500/20' : 'border-white/20'
                              }`}
                            >
                              <div className="flex items-center space-x-3 mb-2">
                                <card.icon className={`w-6 h-6 ${card.iconClass}`} />
                                <div className="text-white font-semibold">{card.title}</div>
                              </div>
                              <div className="text-white/60 text-sm">{card.sub}</div>
                            </button>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {/* Section 2 — Détail état sélectionné */}
              <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
                <button type="button" onClick={() => setEtatsExpandedSections((p) => ({ ...p, detail: !p.detail }))} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <Eye size={20} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white truncate">
                      {selectedEtatType
                        ? ifrsEtatsCards.find((c) => c.id === selectedEtatType)?.title
                        : p('selected_statement_detail')}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedEtatType && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleExportEtat(selectedEtatType); }}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-green-500/20 text-green-200 border border-green-500/30 rounded-xl hover:bg-green-500/30 transition-colors"
                        >
                          <Download size={16} />
                          {p('export_pdf')}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); goToStatementsTab(selectedEtatType); }}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-500/20 text-blue-200 border border-blue-500/30 rounded-xl hover:bg-blue-500/30 transition-colors"
                        >
                          <Eye size={16} />
                          {p('statements_tab_btn')}
                        </button>
                      </>
                    )}
                    {etatsExpandedSections.detail ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
                  </div>
                </button>
                <AnimatePresence>
                  {etatsExpandedSections.detail && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="p-4 pt-0">{renderEtatDetailContent()}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-blue-300 text-sm">
                  <strong>{p('note_prefix')}</strong> {p('etats_modal_note')}
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-2">
              <button type="button" onClick={() => setShowEtatsIFRS(false)} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all font-medium">
                {t('common.close')}
              </button>
              <button type="button" onClick={() => goToStatementsTab(selectedEtatType)} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold shadow-lg shadow-purple-500/30 flex items-center gap-2">
                <BarChart3 size={16} />
                {p('view_all_statements')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};

export default IfrsSupportPage;
